import { useCreateStoryboard } from "@/hooks/storyboards/useCreateStoryboard";
import { useDuplicateStoryboard } from "@/hooks/storyboards/useDuplicateStoryboard";
import { useInitializeStoryboard } from "@/hooks/storyboards/useInitializeStoryboards";
import {
  useCanvasStore,
  useHoveredNavTreeNode,
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import {
  AddJsxChange,
  AddAndRemoveJsxChange,
  AnyChangeLedgerItem,
  DuplicateChange,
  StoryboardDelta,
  UpdateStoryboardsChange,
} from "@/store/zustand/changeLedgerTypes";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { atob_utf8, btoa_utf8 } from "@/utils/bytes";
import { getComponentFileFromNavTreeNodes } from "@/utils/jsx/extractElementsUtils";
import { TempoElement } from "@/utils/tempoElement";
import { NavTreeNode } from "@/utils/types";
import {
  useActiveCanvas,
  useActiveProject,
  useAutoUpdateRef,
  useCanvasId,
} from "@/utils/utils";
import { useCallback } from "react";
import { toast } from "sonner";
import {
  DEFAULT_INSTANT_UPDATE_HTML,
  processInstantUpdateData,
} from "@/utils/navTreeUtils";
import { createInstantUpdateWithShell } from "../IframePool/utils";
import { isNavTreeEmpty } from "../Storyboard/StaticIframe/utils/navTreeLookupUtils";
import { useLoadCodebase } from "@/utils/devServerUtils";
import { v4 as uuidv4 } from "uuid";
import { updateCodebaseId } from "../Storyboard/StaticIframe/utils/utils";
import { useReactComponentHandler } from "@/hooks/useReactComponentHandler";
import { usePagesStore } from "@/store/zustand/pagesStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import {
  generateStoryboardFilename,
  getUniqueName,
} from "@/utils/fileNameUtils";
import { updateAllStoreLookups } from "../Storyboard/StaticIframe/utils/navTreeLookupUtils";
import { useIframePoolStore } from "../IframePool/iframePoolStore";

export const TEMPO_ELEMENT_UNKNOWN_WIDTH_DEFAULT = 800;
export const TEMPO_ELEMENT_UNKNOWN_HEIGHT_DEFAULT = 600;

export interface ClipboardData {
  // Option 1: Inserting something from full file contents
  fileContents?: string;
  fileContentsSourceFilename?: string;

  // Option 2: Inserting something from a codebase id
  insertCodebaseId?: string;

  // Option 3: Inserting a component from reactcomponents.com
  reactComponentsComId?: string;

  // Instant update
  htmlForInstantUpdate?: string;

  // Nav tree _should_ always be provided, but if it's not the storyboard will load until the iframe hot reloads
  compressedNavTreeNodesForInstantUpdate?: string[];

  width?: number;
  height?: number;

  selectedElementKey?: string;
  multiselectedElementKeys?: string[];
  sourceParentElementKey?: string;

  // Optional - if true inserts into the current hovered element
  insertIntoHoveredElement?: boolean;

  // Optional - if true inserts into this specific position and data
  insertIntoPosition?: {
    parentTempoElement?: TempoElement;
    beforeCodebaseId?: string;
    afterCodebaseId?: string;
  };

  // Optional - if true deletes the source storyboard (only works if sourceType is Storyboard)
  deleteSourceStoryboard?: boolean;

  // Optional - the name of the source storyboard to preserve when pasting
  sourceStoryboardName?: string;

  // Optional - if true deletes the source elements after insertion
  deleteSourceElements?: boolean;

  // Optional - if true forces the creation of a new storyboard
  forceCreateStoryboard?: boolean;

  propsToSet?: Record<string, any>;

  // Multi-storyboard paste data -> prioritized over single element paste data
  storyboards?: Array<{
    type: string; // 'APPLICATION' | 'COMPONENT'
    name?: string;
    width: number;
    height: number;
    x: number; // Raw position from source
    y: number;
    zIndex: number;
    route?: string; // APPLICATION only
    fileContents?: string; // COMPONENT only
    fileContentsSourceFilename?: string; // COMPONENT only - source file path
    importName?: string; // COMPONENT only
    htmlForInstantUpdate?: string;
    compressedNavTree?: string;
  }>;
}

export const setTempoClipboardDataOnDrag = (
  e: React.DragEvent,
  data: ClipboardData,
  textToShow?: string,
) => {
  e.dataTransfer.setData("text/plain", textToShow ?? "");
  const stringifiedData = JSON.stringify(data);
  e.dataTransfer.setData(
    "text/html",
    `<!--tempo-metadata:${btoa_utf8(stringifiedData)}-->`,
  );
};

export const setTempoClipboardData = (
  e?: ClipboardEvent,
  data?: ClipboardData,
  textToShow?: string,
) => {
  console.log(
    "Setting tempo clipboard data",
    data,
    data?.fileContents?.length,
    textToShow,
  );
  const stringifiedData = JSON.stringify(data);

  if (e?.clipboardData) {
    e.clipboardData?.setData("text/plain", textToShow ?? "");
    e.clipboardData?.setData(
      "text/html",
      `<!--tempo-metadata:${btoa_utf8(stringifiedData)}-->`,
    );
  }

  navigator.clipboard.writeText(textToShow ?? "");
  navigator.clipboard.write([
    new ClipboardItem({
      "text/html": `<!--tempo-metadata:${btoa_utf8(stringifiedData)}-->`,
    }),
  ]);
};

export const getTempoClipboardDataFromDrop = (
  e: React.DragEvent,
): ClipboardData | null => {
  try {
    const html = e.dataTransfer?.getData("text/html");
    if (!html) {
      return null;
    }
    const match = html.match(/<!--tempo-metadata:([^>]+)-->/);
    if (!match) {
      return null;
    }
    const metadata = JSON.parse(atob_utf8(match[1]));
    return metadata;
  } catch (error) {
    console.error("Error getting tempo clipboard data from drop", error);
    return null;
  }
};

export const getTempoClipboardData = async (
  e: ClipboardEvent,
): Promise<ClipboardData | null> => {
  try {
    // @ts-ignore
    if (navigator.clipboard?.read) {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        if (item.types.includes("text/html")) {
          const blob = await item.getType("text/html");
          const html = await blob.text();
          const match = html.match(/<!--tempo-metadata:([^>]+)-->/);
          if (match) {
            const metadata = JSON.parse(atob_utf8(match[1]));
            return metadata;
          }
        }
      }
    }
  } catch (error) {
    console.error("Error getting tempo clipboard data", error);
  }

  try {
    // @ts-ignore
    const clipboardData = e.clipboardData || window.clipboardData;
    const html = clipboardData?.getData("text/html");
    if (!html) {
      return null;
    }
    const match = html.match(/<!--tempo-metadata:([^>]+)-->/);
    if (!match) {
      return null;
    }
    const metadata = JSON.parse(atob_utf8(match[1]));
    return metadata;
  } catch (error) {
    console.error("Error getting tempo clipboard data", error);
    return null;
  }
};

export interface ExtensionMetadata {
  source: string;
  version: string;
  timestamp: number;
}

export const getExtensionClipboardMetadata = async (
  e: ClipboardEvent,
): Promise<ExtensionMetadata | null> => {
  const parseMetadata = (html: string) => {
    if (!html || typeof html !== "string") return null;

    // Try HTML comment first
    const commentMatch = html.match(
      /<!--tempo(?:-chrome)?-extension-metadata:([\s\S]*?)-->/,
    );
    if (commentMatch?.[1]) {
      try {
        const decoded = atob(commentMatch[1].trim());
        const parsed = JSON.parse(decoded);
        if (parsed?.source === "tempo-chrome-extension") return parsed;
      } catch (e) {
        console.error("[Paste] Invalid metadata", e);
      }
    }

    // Fallback: try data attribute on any div
    const dataMatch = html.match(
      /data-tempo(?:-chrome)?-extension=["']([^"']+)["']/,
    );
    if (dataMatch?.[1]) {
      try {
        const decoded = atob(dataMatch[1]);
        const parsed = JSON.parse(decoded);
        if (parsed?.source === "tempo-chrome-extension") return parsed;
      } catch (e) {
        // Invalid metadata
      }
    }

    return null;
  };

  // 1. Try synchronous event data first
  try {
    // @ts-ignore
    const clipboardData = e.clipboardData || window.clipboardData;
    if (clipboardData) {
      const html = clipboardData.getData("text/html");
      if (html) {
        const metadata = parseMetadata(html);
        if (metadata) return metadata;
      }
    }
  } catch (error) {
    console.error("[Paste] Error parsing metadata", error);
  }

  // 2. Fallback to async clipboard API
  try {
    // @ts-ignore
    if (navigator.clipboard?.read) {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        if (item.types.includes("text/html")) {
          const blob = await item.getType("text/html");
          const html = await blob.text();
          const metadata = parseMetadata(html);
          if (metadata) return metadata;
        }
      }
    }
  } catch (error) {
    console.error("[Paste] Error reading clipboard", error);
  }

  return null;
};

/**
 * Used for either on pasting or dropping into the canvas
 */
export const useInsertClipboardDataIntoCanvas = () => {
  const canvasId = useCanvasId();

  const elementKeyToNavNodeLookup = useCanvasStore(
    (store) => store.elementKeyToNavNodeLookup,
  );
  const elementKeyToNavNodeLookupRef = useAutoUpdateRef(
    elementKeyToNavNodeLookup,
  );

  const storyboardToNavTree = useCanvasStore(
    (store) => store.storyboardToNavTree,
  );
  const storyboardToNavTreeRef = useAutoUpdateRef(storyboardToNavTree);
  const canvasIdRef = useAutoUpdateRef(canvasId);

  const { selectedElement } = useSelectedNavTreeNode();
  const { multiselectedElements } = useMultiselectedNavTreeNode();
  const { hoveredElement } = useHoveredNavTreeNode();
  const selectedElementRef = useAutoUpdateRef(selectedElement);
  const hoveredElementRef = useAutoUpdateRef(hoveredElement);
  const multiselectedElementsRef = useAutoUpdateRef(multiselectedElements);

  const initializeStoryboard = useInitializeStoryboard();
  const createStoryboard = useCreateStoryboard();
  const initializeStoryboardRef = useAutoUpdateRef(initializeStoryboard);
  const createStoryboardRef = useAutoUpdateRef(createStoryboard);

  const project = useActiveProject();
  const projectRef = useAutoUpdateRef(project);

  const duplicateStoryboard = useDuplicateStoryboard();

  const activeCanvas = useActiveCanvas();
  const activeCanvasRef = useAutoUpdateRef(activeCanvas);

  const { loadComponent, installDepsForComponent } = useReactComponentHandler();

  // Get the current page ID for creating storyboards
  const selectedPageId = usePagesStore(
    (state) => state.selectedPageId[canvasId],
  );
  const selectedPageIdRef = useAutoUpdateRef(selectedPageId);

  /**
   * Insert storyboards from clipboard data (for pasting entire storyboards)
   */
  const insertStoryboardsFromClipboard = useCallback(
    async (storyboards: NonNullable<ClipboardData["storyboards"]>) => {
      const { x: mouseX, y: mouseY } = useCanvasStore.getState().canvasMousePos;
      const pagesStore = usePagesStore.getState();
      const currentPage = pagesStore.pages[canvasIdRef.current]?.find(
        (p) => p.id === selectedPageIdRef.current,
      );

      if (!currentPage) {
        // This should never happen since there should never be an unselected page
        toast.error("Please select a page before pasting storyboards");
        return;
      }

      // Calculate bounding box from raw positions (top-left becomes mouse position)
      const minX = Math.min(...storyboards.map((sb) => sb.x));
      const minY = Math.min(...storyboards.map((sb) => sb.y));

      // Get existing storyboard names for uniqueness check
      const existingNames = new Set(
        currentPage.storyboards
          .map((sb) => sb.name)
          .filter(Boolean) as string[],
      );

      // Get max zIndex
      const maxZIndex = Math.max(
        0,
        ...currentPage.storyboards.map((sb) => sb.zIndex),
      );

      // Build deltas for all storyboards
      const deltas: StoryboardDelta[] = [];

      for (let i = 0; i < storyboards.length; i++) {
        const sb = storyboards[i];
        const newId = uuidv4();
        const uniqueName = getUniqueName(
          sb.name || "Pasted Storyboard",
          existingNames,
        );
        existingNames.add(uniqueName); // Track for subsequent storyboards

        const delta: StoryboardDelta = {
          changeType: "upsert",
          storyboardId: newId,
          pageId: currentPage.id,
          pageConfigPath: currentPage.configPath,
          storyboardUpdateDict: {
            id: newId,
            type: sb.type as any,
            name: uniqueName,
            width: sb.width,
            height: sb.height,
            x: mouseX + (sb.x - minX), // Offset calculated at paste time
            y: mouseY + (sb.y - minY),
            zIndex: maxZIndex + i + 1,
          },
        };

        // APPLICATION storyboards: just add route
        if (sb.type === STORYBOARD_TYPES.APPLICATION) {
          delta.storyboardUpdateDict.route = sb.route;
        }

        // COMPONENT storyboards: create backing file
        if (sb.type === STORYBOARD_TYPES.COMPONENT) {
          if (sb.fileContents) {
            // Compute file path using same pattern as useCreateStoryboard
            const pageFolder = currentPage.configPath.substring(
              0,
              currentPage.configPath.lastIndexOf("/"),
            );
            const filename = generateStoryboardFilename(
              uniqueName,
              newId,
              ".tsx",
            );

            delta.newFilePath = `${pageFolder}/${filename}`;
            delta.newFileContents = sb.fileContents;
            // Pass source filename for import rewriting on backend
            if (sb.fileContentsSourceFilename) {
              delta.newFileContentsSourceFilename =
                sb.fileContentsSourceFilename;
            }
            // These properties are understood by the backend but not in the frontend Storyboard type
            (delta.storyboardUpdateDict as any).importPath = `./${filename}`;

            if (sb.importName) {
              (delta.storyboardUpdateDict as any).importName = sb.importName;
            }
          }
        }

        // Set up instant update preview for pasted storyboard
        // Use the captured HTML from copy, processed through same flow as duplicate
        if (sb.htmlForInstantUpdate) {
          const { html: processedHtml, navTree: processedNavTree } =
            processInstantUpdateData({
              storyboardId: newId,
              canvasId: canvasIdRef.current,
              instantUpdateData: {
                html: sb.htmlForInstantUpdate,
                compressedNavTreeNodes: sb.compressedNavTree
                  ? [sb.compressedNavTree]
                  : undefined,
              },
              wrapInShell: true, // Same as duplicate storyboard
            });

          if (processedHtml) {
            useIframePoolStore
              .getState()
              .setHtmlForStoryboard(newId, processedHtml);
          }

          if (processedNavTree) {
            updateAllStoreLookups(newId, { navTree: processedNavTree }, true);
          }
        } else if (sb.compressedNavTree) {
          // Fallback: only nav tree available (shouldn't happen normally)
          const htmlShell = useIframePoolStore.getState().latestHtmlShell;
          if (htmlShell) {
            const placeholderHtml = createInstantUpdateWithShell(
              htmlShell,
              DEFAULT_INSTANT_UPDATE_HTML,
            );
            useIframePoolStore
              .getState()
              .setHtmlForStoryboard(newId, placeholderHtml);
          }

          const { navTree: processedNavTree } = processInstantUpdateData({
            storyboardId: newId,
            canvasId: canvasIdRef.current,
            instantUpdateData: {
              compressedNavTreeNodes: [sb.compressedNavTree],
            },
            wrapInShell: false,
          });

          if (processedNavTree) {
            updateAllStoreLookups(newId, { navTree: processedNavTree }, true);
          }
        }

        deltas.push(delta);
      }

      // Execute single change with all storyboards
      const change = new UpdateStoryboardsChange({
        canvasId: canvasIdRef.current,
        deltas,
        changeTypeOverride: "PASTE_STORYBOARDS",
      });

      useDesignCommandManager.getState().executeCommand(change);
    },
    [canvasIdRef, selectedPageIdRef],
  );

  /**
   * Insert contents into the canvas
   *
   * @param fileContents the file contents to extract the code to insert from
   * @param fileContentsSourceFilename the file path the source is from (for new import paths)
   * @param htmlForInstantUpdate the html for the instant update preview
   * @param width the width of the element to paste
   * @param height the height of the element to paste
   * @param sourceElementKeys if from a copy/paste, the element keys that this was sourced from
   *
   */
  const insertClipboardDataIntoCanvas = useCallback(
    async (tempoCopyData: ClipboardData) => {
      // Handle storyboard paste first
      if (tempoCopyData.storyboards?.length) {
        await insertStoryboardsFromClipboard(tempoCopyData.storyboards);
        return;
      }
      const {
        fileContents: _fileContents,
        fileContentsSourceFilename: _fileContentsSourceFilename,
        insertCodebaseId,
        selectedElementKey: copiedSelectedElementKey,
        multiselectedElementKeys: copiedMultiselectedElementKeys,
        htmlForInstantUpdate,
        compressedNavTreeNodesForInstantUpdate,
        width,
        height,
        insertIntoHoveredElement,
        forceCreateStoryboard,
        propsToSet,
        deleteSourceElements,
        deleteSourceStoryboard,
        sourceStoryboardName,
        insertIntoPosition,
        reactComponentsComId,
      } = tempoCopyData;

      const canvasMousePos = useCanvasStore.getState().canvasMousePos;

      let fileContentsToUse = _fileContents;
      let filePathToUse: string | undefined = _fileContentsSourceFilename;
      let extraPathToContents: Record<string, string> = {};

      // For copied elements: if they haven't been loaded yet, make an API request to load them
      if (
        (!fileContentsToUse || !filePathToUse) &&
        !insertCodebaseId &&
        (copiedMultiselectedElementKeys?.length || copiedSelectedElementKey)
      ) {
        let copiedNavTreeNodes: NavTreeNode[] = [];
        if (copiedMultiselectedElementKeys?.length) {
          copiedNavTreeNodes = copiedMultiselectedElementKeys.map(
            (key) => elementKeyToNavNodeLookupRef.current[key],
          );
        } else if (copiedSelectedElementKey) {
          copiedNavTreeNodes = [
            elementKeyToNavNodeLookupRef.current[copiedSelectedElementKey],
          ];
        }

        if (
          !copiedNavTreeNodes?.length ||
          copiedNavTreeNodes.filter((n) => n).length !==
            copiedNavTreeNodes.length
        ) {
          toast.error(
            "Copied elements cannot be found. Please re-select the elements to copy and try again.",
          );
          return;
        }

        const res = await getComponentFileFromNavTreeNodes(
          canvasIdRef.current,
          copiedNavTreeNodes,
          false,
        );

        if (res) {
          fileContentsToUse = res.newFileContents;
          filePathToUse = res.dummyFilePath || undefined;
        } else {
          toast.error(
            "Error generating code from copied elements. Please try again.",
          );
          return;
        }

        if (!fileContentsToUse || !filePathToUse) {
          toast.error(
            "Error copying elements, cannot create code from copied element",
          );
          return;
        }
      }

      // For react components.com: load the file contents for the component
      if (reactComponentsComId) {
        const result = await loadComponent(reactComponentsComId);

        if (!result?.success) {
          // most likely user cancelled, other error states handled within
          toast.error("Error loading component from reactcomponents.com");
          return;
        }

        fileContentsToUse = result.contents;
        extraPathToContents = result.extraPathToContents || {};

        const { success, nonShadcnFiles } = await installDepsForComponent(
          result.componentData,
        );

        if (!success) {
          // user cancelled
          toast.error(
            "Error installing dependencies for component from reactcomponents.com",
          );
          return;
        }

        extraPathToContents = nonShadcnFiles || {};
      }

      // Sometimes no need for a source filename, just use a dummy one
      if (fileContentsToUse && !filePathToUse) {
        filePathToUse = "index.tsx";
      }

      const sourceElementKeys = (
        copiedMultiselectedElementKeys?.length
          ? copiedMultiselectedElementKeys
          : [copiedSelectedElementKey]
      ).filter(Boolean) as string[];

      const allSourceElementKeys = new Set(sourceElementKeys);

      let elementsToInsertInto = insertIntoHoveredElement
        ? new Set([hoveredElementRef.current?.getKey()])
        : new Set([
            ...(multiselectedElementsRef.current?.map((e) => e.getKey()) || []),
            selectedElementRef.current.getKey(),
          ]);
      // Get the drop position from the store
      let dropPosition = useCanvasStore.getState().dropPosition;

      if (insertIntoPosition?.parentTempoElement) {
        elementsToInsertInto = new Set([
          insertIntoPosition.parentTempoElement.getKey(),
        ]);
        dropPosition = {
          beforeCodebaseId: insertIntoPosition.beforeCodebaseId,
          afterCodebaseId: insertIntoPosition.afterCodebaseId,
        };
      }

      const overlap = Array.from(allSourceElementKeys).some((key) =>
        elementsToInsertInto.has(key as string),
      );

      let mainElementToInsertInto = TempoElement.fromKey(
        Array.from(elementsToInsertInto)[0] || "",
      );

      const navTreeNodeToInsertInto =
        elementKeyToNavNodeLookupRef.current[mainElementToInsertInto.getKey()];

      // Special case - if the destination element is the top item in the storyboard and there is overlap,
      // duplicate the storyboard
      if (
        navTreeNodeToInsertInto &&
        !navTreeNodeToInsertInto?.parent &&
        overlap
      ) {
        const sourceStoryboardId = TempoElement.fromKey(
          sourceElementKeys[0],
        ).storyboardId;
        const sourceStoryboard = activeCanvasRef.current?.storyboards.find(
          (storyboard) => storyboard.id === sourceStoryboardId,
        );
        if (sourceStoryboard) {
          duplicateStoryboard(sourceStoryboard);
        }
        return;
      }

      if (!forceCreateStoryboard && mainElementToInsertInto?.isStoryboard()) {
        // Pick the top level node in the storyboard
        const topNavTreeNode =
          storyboardToNavTreeRef.current[mainElementToInsertInto.storyboardId];
        if (!topNavTreeNode || isNavTreeEmpty(topNavTreeNode)) {
          toast.error(
            "Cannot insert into an empty storyboard, please create a new one",
          );
          return;
        }

        mainElementToInsertInto = topNavTreeNode.tempoElement;
      }

      // Inserting into an element inside a storyboard
      if (!forceCreateStoryboard && mainElementToInsertInto?.isKnownElement()) {
        if (overlap) {
          useDesignCommandManager.getState().executeCommand(
            new DuplicateChange({
              codebaseIdsToDuplicate: Array.from(allSourceElementKeys)
                .map((key) => TempoElement.fromKey(key).codebaseId)
                .filter(Boolean),
              codebaseIdHashingKey: uuidv4(),
            }),
          );
          return;
        }

        const codebaseIdToAddTo =
          navTreeNodeToInsertInto.tempoElement.codebaseId;

        const {
          html: processedHtml,
          navTree: processedNavTree,
          error,
        } = processInstantUpdateData({
          storyboardId: mainElementToInsertInto.storyboardId,
          canvasId: canvasIdRef.current,
          projectSubfolderPath: projectRef.current?.subfolderPath,
          instantUpdateData: {
            html: htmlForInstantUpdate ?? "",
            compressedNavTreeNodes: compressedNavTreeNodesForInstantUpdate,
          },
          wrapInShell: false,
        });

        if (error) {
          toast.error(error);
          return;
        }

        let change: AnyChangeLedgerItem;

        // If deleteSourceElements is true, create an AddAndRemoveJsxChange
        const codebaseIdHashingKey = uuidv4();
        if (deleteSourceElements && sourceElementKeys?.length) {
          const sourceCodebaseIds = sourceElementKeys
            .map((key) => TempoElement.fromKey(key).codebaseId)
            .filter(Boolean);

          change = new AddAndRemoveJsxChange({
            addJsxChangeFields: {
              codebaseIdToAddTo: codebaseIdToAddTo,
              fileContentsToSourceFrom: fileContentsToUse,
              fileContentsSourceFilename: filePathToUse,
              htmlForInstantUpdate: processedHtml,
              navTreeNodesForInstantUpdate: processedNavTree
                ? [processedNavTree]
                : undefined,
              addCodebaseId: insertCodebaseId,
              propsToSet,
              beforeCodebaseId: dropPosition?.beforeCodebaseId,
              afterCodebaseId: dropPosition?.afterCodebaseId,
              codebaseIdHashingKey,
            },
            removeJsxChangeFields: {
              codebaseIdsToRemove: sourceCodebaseIds,
            },
          });
        } else {
          // Standard add operation
          change = new AddJsxChange({
            codebaseIdToAddTo: codebaseIdToAddTo,
            fileContentsToSourceFrom: fileContentsToUse,
            fileContentsSourceFilename: filePathToUse,
            htmlForInstantUpdate: processedHtml,
            navTreeNodesForInstantUpdate: processedNavTree
              ? [processedNavTree]
              : undefined,
            addCodebaseId: insertCodebaseId,
            propsToSet,
            beforeCodebaseId: dropPosition?.beforeCodebaseId,
            afterCodebaseId: dropPosition?.afterCodebaseId,
            codebaseIdHashingKey,
          });
        }

        // Dropping storyboard into another - delete the source storyboard
        const sourceElementKey =
          sourceElementKeys?.length === 1
            ? TempoElement.fromKey(sourceElementKeys[0])
            : undefined;
        if (deleteSourceStoryboard && sourceElementKey?.storyboardId) {
          // Find which page contains the source storyboard
          const pagesStore = usePagesStore.getState();
          const pages = pagesStore.pages[canvasIdRef.current] || [];
          let sourcePageId: string | undefined;
          let sourcePageConfigPath: string | undefined;

          for (const page of pages) {
            if (
              page.storyboards.some(
                (sb) => sb.id === sourceElementKey.storyboardId,
              )
            ) {
              sourcePageId = page.id;
              sourcePageConfigPath = page.configPath;
              break;
            }
          }

          if (!sourcePageId) {
            toast.error(
              "Could not find the page containing the source storyboard",
            );
            return;
          }

          const storyboardDeltas: StoryboardDelta[] = [
            {
              changeType: "update",
              storyboardId: sourceElementKey.storyboardId,
              pageId: sourcePageId,
              pageConfigPath: sourcePageConfigPath,
              storyboardUpdateDict: {
                deleted: true,
                deleted_at: new Date().toISOString(),
              },
            },
          ];

          change.changeFields.deletedStoryboardId =
            sourceElementKey.storyboardId;

          change = new UpdateStoryboardsChange({
            canvasId: canvasIdRef.current,
            deltas: storyboardDeltas,
            additionalGroupedChange: change,
          });
        }

        if (processedNavTree?.tempoElement?.codebaseId) {
          let expectedIndex = navTreeNodeToInsertInto?.children?.length;
          if (dropPosition?.beforeCodebaseId) {
            const beforeIndex = navTreeNodeToInsertInto?.children?.findIndex(
              (child) =>
                child.tempoElement?.codebaseId ===
                dropPosition.beforeCodebaseId,
            );
            if (beforeIndex !== -1) {
              expectedIndex = beforeIndex;
            }
          } else if (dropPosition?.afterCodebaseId) {
            const afterIndex = navTreeNodeToInsertInto?.children?.findIndex(
              (child) =>
                child.tempoElement?.codebaseId === dropPosition.afterCodebaseId,
            );
            if (afterIndex !== -1) {
              expectedIndex = afterIndex + 1;
            }
          }

          const indexOfRemove = navTreeNodeToInsertInto?.children?.findIndex(
            (child) =>
              child.tempoElement?.codebaseId ===
              processedNavTree?.tempoElement?.codebaseId,
          );
          if (indexOfRemove !== -1 && indexOfRemove < expectedIndex) {
            expectedIndex -= 1;
          }

          const expectedUniquePath = `${mainElementToInsertInto.uniquePath}-${expectedIndex}`;
          change.setSelectedElementsAfterInstantUpdate(
            new TempoElement(
              updateCodebaseId(
                processedNavTree?.tempoElement?.codebaseId || "",
                codebaseIdHashingKey,
              ),
              mainElementToInsertInto.storyboardId,
              expectedUniquePath,
            ).getKey(),
            [],
          );
        }

        useDesignCommandManager.getState().executeCommand(change);

        // Clear the drop position after using it
        useCanvasStore.getState().setDropPosition({});
        return;
      }

      if (overlap) {
        const sourceStoryboardId = TempoElement.fromKey(
          sourceElementKeys[0],
        ).storyboardId;
        const sourceStoryboard = activeCanvasRef.current?.storyboards.find(
          (storyboard) => storyboard.id === sourceStoryboardId,
        );
        if (sourceStoryboard) {
          duplicateStoryboard(sourceStoryboard);
        }
        return;
      }

      // Inserting onto the canvas, make a new storyboard
      const { x, y } = canvasMousePos;

      const left = x - (width ? width / 2 : 0);
      const top = y - (height ? height / 2 : 0);

      const newStoryboard = initializeStoryboardRef.current(
        left,
        top,
        width || TEMPO_ELEMENT_UNKNOWN_WIDTH_DEFAULT,
        height || TEMPO_ELEMENT_UNKNOWN_HEIGHT_DEFAULT,
        undefined, // idToUse
        sourceStoryboardName, // preserve name from source storyboard
      );

      // Convert source element keys to codebase IDs if deleteSourceElements is true
      const sourceCodebaseIdsToRemove =
        deleteSourceElements && sourceElementKeys?.length
          ? sourceElementKeys
              .map((key) => TempoElement.fromKey(key).codebaseId)
              .filter(Boolean)
          : undefined;

      if (!selectedPageIdRef.current) {
        toast.error("Please select a page before pasting a storyboard");
        return;
      }

      createStoryboardRef.current({
        storyboard: newStoryboard,
        pageId: selectedPageIdRef.current,
        fileContentsToCreateWith: fileContentsToUse,
        fileContentsSourceFilename: filePathToUse,
        actionText: "Pasted Tempo Component",
        componentToAdd: insertCodebaseId
          ? {
              componentId: insertCodebaseId,
              propsToSet,
            }
          : undefined,
        instantUpdateData: {
          html: htmlForInstantUpdate ?? "",
          compressedNavTreeNodes: compressedNavTreeNodesForInstantUpdate,
        },
        sourceCodebaseIdsToRemove,
        extraPathToContents,
      });
    },
    [
      hoveredElementRef,
      multiselectedElementsRef,
      selectedElementRef,
      elementKeyToNavNodeLookupRef,
      initializeStoryboardRef,
      createStoryboardRef,
      canvasIdRef,
      loadComponent,
      installDepsForComponent,
      activeCanvasRef,
      duplicateStoryboard,
      storyboardToNavTreeRef,
      projectRef,
      selectedPageIdRef,
      insertStoryboardsFromClipboard,
    ],
  );

  return {
    insertClipboardDataIntoCanvas,
  };
};
